import {t} from 'i18next';
import {RegisterOptions, UseFormReturn} from 'react-hook-form';
import {DynamicFormFieldShape} from '.';
import {CarePlanConfigurationFeature} from '../../backend/enums/care-plan-configuration-feature';
import {CarePlanConfigurationType} from '../../backend/enums/care-plan-configuration-type';
import {CarePlanConfiguration} from '../../backend/interfaces/care-plan-configuration';
import {CarePlanConfigurationItem} from '../../backend/interfaces/care-plan-configuration-item';
import {translations} from '../../i18n/translations';
import {
  CAREPLAN_TASKS_FIELD,
  CUSTOM_NAME_MAX_CHARACTERS,
  MEALS_GROUP_ID,
  MEALS_SECTOR_ID,
} from '../../utils/constants';
import {ActivityGroupScheduleFormProps} from '../activity-group-schedule-form/activity-group-schedule-form';
import {ActivityTypePosologyScheduleFormProps} from '../activity-type-posology-schedule-form';
import {FlatInputProps} from '../flat-input/flat-input';
import {FlatMultiTextSelectProps} from '../flat-multi-text-select/flat-multi-text-select';
import {Option, SelectDropdownProps} from '../flat-select-dropdown/flat-select-dropdown';
import {ScheduleFieldProps} from '../schedule-field/schedule-field';
import {ConfigurationsIndex} from './../../types/configurations';
import {SchedulerTypes} from './../../types/schedule';
import {ActivityTypeScheduleFormProps} from './../activity-type-schedule-form/activity-type-schedule-form';
import {FlatInputFeatureAutocompleteProps} from './feature-fields/autocomplete';

/**
 *
 * Handles the handover of component props per field.
 *
 * */

export const getFieldTypeProps = (
  field: CarePlanConfiguration,
  required: boolean,
  configurationsIndex: ConfigurationsIndex,
  contextualProps: {
    formInstance: UseFormReturn<DynamicFormFieldShape>;
    activityGroupId: string;
    activityTypeId: string;
    activitiesSectorId: string;
    carePlanConfigurationId: string;
    configuredRepetition?: 'activities-group' | 'activity-type';
    editMode?: boolean;
  }
) => {
  let genericProps = {
    id: field.id,
    label: field.name,
    required,
    placeholder: field.description ?? undefined,
    readOnly: contextualProps?.editMode,
    disabled: contextualProps?.editMode,
    rows: field?.rowsCount ? field.rowsCount : undefined,
    multiline: field?.rowsCount ? true : undefined,
  };

  switch (field.type) {
    case CarePlanConfigurationType.Integer:
    case CarePlanConfigurationType.Number: {
      let props: FlatInputProps = {
        ...genericProps,
        type: 'number',
      };

      return props;
    }
    case CarePlanConfigurationType.String: {
      let props: FlatInputProps = {
        ...genericProps,
        type: 'text',
      };

      if (field?.feature) {
        const value = contextualProps.formInstance.getValues(props.id!);

        if (field.feature === CarePlanConfigurationFeature.Notes) {
          props.counter = contextualProps.formInstance.formState.errors[props.id!]
            ? {
                current: value ? String(value).length : 0,
                max: CAREPLAN_TASKS_FIELD.FEATURE.NOTES,
              }
            : undefined;
        }

        if (field.feature === CarePlanConfigurationFeature.Name) {
          props.counter = contextualProps.formInstance.formState.errors[props.id!]
            ? {
                current: value ? String(value).length : 0,
                max: CUSTOM_NAME_MAX_CHARACTERS,
              }
            : undefined;
          props.placeholder = t(translations.configurations_fields.feature.name.placeholder);
        }
      }

      return props;
    }

    case CarePlanConfigurationType.Autocomplete: {
      const props: Partial<FlatInputFeatureAutocompleteProps> = {
        ...genericProps,
        fetchParams: contextualProps,
      };
      return props;
    }

    case CarePlanConfigurationType.ActivitiesGroupSchedule: {
      const props: Partial<ActivityGroupScheduleFormProps> & Partial<ScheduleFieldProps> = {
        ...genericProps,
        type: SchedulerTypes.ActivityGroup,
        parentFormInstance: contextualProps.formInstance,
      };

      return props;
    }

    case CarePlanConfigurationType.ActivityTypeSchedule: {
      const props: Partial<ActivityTypeScheduleFormProps> & Partial<ScheduleFieldProps> = {
        ...genericProps,
        type: SchedulerTypes.ActivityType,
        parentFormInstance: contextualProps.formInstance,
        disabledRepetition: contextualProps?.configuredRepetition === 'activities-group',
      };

      // pass down field ids for context -> might re-evaluate this by wrapping the form with a react context layer to avoid injection
      configurationsIndex &&
        Object.entries(configurationsIndex).map(([id, field]) => {
          if (field?.feature) {
            if (field.feature === CarePlanConfigurationFeature.StartsAtDate)
              props.startAtDateId = id;
            if (field.feature === CarePlanConfigurationFeature.StartsAtTime)
              props.startAtTimeId = id;
          }
          return undefined;
        });

      return props;
    }

    case CarePlanConfigurationType.PosologySchedule: {
      const props: Partial<ActivityTypePosologyScheduleFormProps> & Partial<ScheduleFieldProps> = {
        ...genericProps,
        parentFormInstance: contextualProps.formInstance,
        activitySectorId: MEALS_SECTOR_ID,
        activityGroupId: MEALS_GROUP_ID,
        disabledRepetition: contextualProps?.configuredRepetition === 'activities-group',
      };

      // pass down field ids for context
      configurationsIndex &&
        Object.entries(configurationsIndex).map(([id, field]) => {
          if (field?.feature) {
            if (field.feature === CarePlanConfigurationFeature.StartsAtDate)
              props.startAtDateId = id;
            if (field.feature === CarePlanConfigurationFeature.StartsAtTime)
              props.startAtTimeId = id;
          }
          return undefined;
        });

      return props;
    }

    case CarePlanConfigurationType.Array: {
      let props: Partial<FlatMultiTextSelectProps> | Partial<SelectDropdownProps> = {
        ...genericProps,
        multiple: field.multipleValues || false,
        options: field.arrayItems
          ? (field.arrayItems.map((item: CarePlanConfigurationItem) => {
              if (item.isDeprecated) return null;

              let option: Option = {
                key: item.id ?? item.name,
                label: item.name,
              };

              if (field?.feature) {
                if (field.feature === CarePlanConfigurationFeature.AutonomyAssessment) {
                  option.selectedColor = item.color;
                }
              }

              return option;
            }) as Option[])
          : undefined,
      };

      return props;
    }

    case CarePlanConfigurationType.Date:
    case CarePlanConfigurationType.Time: {
      return {
        ...genericProps,
        noUtc: true,
      };
    }

    default:
      return genericProps;
  }
};

/**
 *
 * Handles the handover of validation rules per field.
 *
 * */

export const getFieldValidations = (
  field: CarePlanConfiguration,
  required: boolean,
  configuredRepetition?: 'activities-group' | 'activity-type'
) => {
  let requiredFlag = required ?? false;

  if (
    field?.feature === CarePlanConfigurationFeature.ActivationState ||
    field.type === CarePlanConfigurationType.Boolean ||
    (field.feature === CarePlanConfigurationFeature.Schedule &&
      configuredRepetition === 'activities-group')
  ) {
    requiredFlag = false;
  }

  let rules = {
    required: {
      value: requiredFlag,
      message: t(translations.dynamic_form.required_message),
    },
  } as Omit<
    RegisterOptions<any, string>,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
  >;

  if (field?.feature) {
    if (field.feature === CarePlanConfigurationFeature.Notes) {
      rules.maxLength = {
        value: CAREPLAN_TASKS_FIELD.FEATURE.NOTES,
        message: t(translations.dynamic_form.pattern_message.max_characters, {
          max: CAREPLAN_TASKS_FIELD.FEATURE.NOTES,
        }),
      };
    }

    if (field.feature === CarePlanConfigurationFeature.Name) {
      rules.maxLength = {
        value: CUSTOM_NAME_MAX_CHARACTERS,
        message: t(translations.dynamic_form.pattern_message.max_characters, {
          max: CUSTOM_NAME_MAX_CHARACTERS,
        }),
      };
    }
  }

  return rules;
};
